import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "../context/I18nContext";
import { IMAGES } from "../lib/images";
import { Overline, Reveal } from "./Reveal";

function ArrowLink({ children, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group mt-8 inline-flex items-center gap-3 border-b border-terra pb-1.5 text-sm font-medium tracking-wide text-charcoal transition-all duration-300 hover:gap-5 hover:text-terra"
    >
      {children}
      <span className="text-terra transition-transform duration-300 group-hover:translate-x-1">→</span>
    </button>
  );
}

function ExperienceRow({ index, reverse, image, imageAlt, block, list, badge, note, onCta, ctaTestid }) {
  return (
    <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
      <Reveal className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`} y={40}>
        <div className="group relative overflow-hidden">
          <motion.img
            src={image}
            alt={imageAlt}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
          />
          <span className="absolute left-5 top-5 bg-charcoal/70 px-3 py-1 font-corm text-xl italic text-cream">
            {index}
          </span>
          {badge && (
            <span
              data-testid={`${ctaTestid}-badge`}
              className="absolute bottom-5 left-5 bg-terra px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-cream"
            >
              {badge}
            </span>
          )}
        </div>
      </Reveal>
      <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
        <Reveal delay={0.1}>
          <Overline>{block.tag}</Overline>
        </Reveal>
        <Reveal delay={0.16}>
          <h3 className="mt-5 font-serif text-3xl leading-tight md:text-4xl">{block.title}</h3>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-5 max-w-md leading-relaxed text-charcoal/70">{block.text}</p>
        </Reveal>
        {note && (
          <Reveal delay={0.26}>
            <p
              data-testid={`${ctaTestid}-note`}
              className="mt-5 max-w-md border-l-2 border-terra/60 pl-4 text-xs leading-relaxed text-charcoal/60"
            >
              {note}
            </p>
          </Reveal>
        )}
        {list && (
          <Reveal delay={0.28}>
            <p className="mt-7 text-[11px] font-semibold uppercase tracking-[0.22em] text-stonekw">
              {block.forWhom}
            </p>
            <ul className="mt-4 grid max-w-md grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {list.map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-charcoal/75">
                  <span className="h-px w-4 bg-terra" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        )}
        <Reveal delay={0.34}>
          <ArrowLink onClick={onCta} testid={`${ctaTestid}-cta`}>
            {block.cta}
          </ArrowLink>
        </Reveal>
      </div>
    </div>
  );
}

export default function TwoWays() {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <section id="experiencias" data-testid="experiences-section" className="bg-bone py-24 text-charcoal md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mb-16 max-w-3xl md:mb-24">
          <Reveal>
            <Overline>{t.twoWays.overline}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">
              {t.twoWays.title}
            </h2>
          </Reveal>
        </div>
        <div className="space-y-20 md:space-y-28">
          <ExperienceRow
            index="01"
            image={IMAGES.expEdouard}
            imageAlt={t.twoWays.block1.imageAlt}
            block={t.twoWays.block1}
            badge={t.twoWays.block1.location}
            note={t.twoWays.block1.note}
            onCta={() => navigate("/reservas?tipo=edouard")}
            ctaTestid="experience-edouard-house"
          />
          <ExperienceRow
            index="02"
            reverse
            image={IMAGES.expHome}
            imageAlt={t.twoWays.block2.imageAlt}
            block={t.twoWays.block2}
            list={t.twoWays.block2.list}
            onCta={() => navigate("/reservas?tipo=domicilio")}
            ctaTestid="experience-your-home"
          />
        </div>
      </div>
    </section>
  );
}
